import type { TicTacToeView } from '@minigame-race/contracts';
import { TurnMoveTimer } from './TurnMoveTimer.js';

const MARKS = ['✕', '◯'];
const YOU_COLOR = '#a6ff2e';
const RIVAL_COLOR = '#22e0ff';
const colorFor = (seat: number, yourSeat: number): string => (seat === yourSeat ? YOU_COLOR : RIVAL_COLOR);

export function TicTacToeBoard({
  view,
  yourSeat,
  yourTurn,
  onMove,
}: {
  view: TicTacToeView;
  yourSeat: number;
  yourTurn: boolean;
  onMove: (move: unknown) => void;
}) {
  const { cells, winLine, moveMsLeft, moveCapMs } = view;
  const win = new Set(winLine ?? []);

  return (
    <div style={{ width: 'min(88cqw, 74cqh, 360px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'min(2cqh,14px)' }}>
      <TurnMoveTimer yourTurn={yourTurn} moveMsLeft={moveMsLeft} moveCapMs={moveCapMs} boardSig={cells.join(',')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        {cells.map((v, i) => {
          const playable = yourTurn && v === -1;
          return (
            <button
              key={i}
              type="button"
              disabled={!playable}
              onClick={() => onMove({ type: 'mark', cell: i })}
              aria-label={`Cell ${i + 1}`}
              style={{
                // A square cell that never changes size: aspect-ratio + a fixed
                // grid layout means an empty cell and a marked cell are identical
                // boxes, so nothing shifts when a mark lands.
                aspectRatio: '1 / 1',
                width: '100%',
                padding: 0,
                borderRadius: 12,
                border: `2px solid ${win.has(i) ? '#fff' : playable ? 'rgba(166,255,46,.45)' : 'rgba(166,255,46,.16)'}`,
                background: win.has(i) ? 'rgba(166,255,46,.16)' : 'rgba(255,255,255,.03)',
                color: v === -1 ? 'transparent' : colorFor(v, yourSeat),
                // Glyph sized to the cell (~52% of a third of the board) so it
                // always fits inside the square regardless of screen size.
                fontSize: 'min(16vw, 62px)',
                fontWeight: 800,
                lineHeight: 1,
                cursor: playable ? 'pointer' : 'default',
                display: 'grid',
                placeItems: 'center',
                touchAction: 'manipulation',
                transition: 'border-color .15s ease, background .15s ease',
              }}
            >
              {v === -1 ? '' : MARKS[v]}
            </button>
          );
        })}
      </div>
      <div style={{ textAlign: 'center', fontSize: 12, color: 'rgba(234,255,234,.5)' }}>
        You are <span style={{ color: YOU_COLOR, fontWeight: 700 }}>{MARKS[yourSeat]}</span>
      </div>
    </div>
  );
}
